import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, LayoutGrid, List, DollarSign, GitBranch } from 'lucide-react';
import { apiRequest } from '../../config/api';
import { useToast } from '../../context/ToastContext';
import usePipelines from '../../hooks/usePipelines';
import PipelineBoard from '../../components/crm/PipelineBoard';
import PipelineListView from '../../components/crm/PipelineListView';

export default function AbleCRMPipelineView() {
  const { pipelineId } = useParams();
  const navigate = useNavigate();
  const { showError } = useToast();
  const { moveDeal } = usePipelines();

  const [pipeline, setPipeline] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('kanban');

  const fetchPipeline = useCallback(async () => {
    try {
      const response = await apiRequest(`/crm/pipelines/${pipelineId}`);
      if (response.ok) {
        const data = await response.json();
        setPipeline(data);
      } else {
        showError('Pipeline not found');
        navigate('/data/pipelines');
      }
    } catch (error) {
      navigate('/data/pipelines');
    }
  }, [pipelineId, showError, navigate]);

  const fetchDeals = useCallback(async () => {
    try {
      const response = await apiRequest(`/crm/deals/?pipeline_id=${pipelineId}`);
      if (response.ok) {
        const data = await response.json();
        setDeals(data);
      }
    } catch (error) {
      showError('Failed to load deals');
    }
  }, [pipelineId, showError]);

  useEffect(() => {
    Promise.all([fetchPipeline(), fetchDeals()]).then(() => setLoading(false));
  }, [fetchPipeline, fetchDeals]);

  const handleMoveDeal = async (dealId, stageId) => {
    setDeals((prev) =>
      prev.map((d) => {
        if (d.id === dealId) {
          const newStage = pipeline?.stages?.find((s) => s.id === stageId);
          return {
            ...d,
            stage_id: stageId,
            stage_name: newStage?.name || d.stage_name,
            stage_color: newStage?.color || d.stage_color,
          };
        }
        return d;
      })
    );

    const result = await moveDeal(dealId, stageId);
    if (!result) {
      fetchDeals();
    }
  };

  const handleDealClick = (deal) => {
    navigate(`/crm/companies/${deal.company_id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-500"></div>
      </div>
    );
  }

  if (!pipeline) return null;

  const totalDeals = deals.length;
  const totalValue = deals.reduce((sum, d) => sum + (d.value || 0), 0);
  const formattedTotal = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(totalValue);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/data/pipelines')}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-lime-50 dark:bg-lime-900/20 flex items-center justify-center">
              <GitBranch className="w-5 h-5 text-lime-600 dark:text-lime-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {pipeline.name}
              </h2>
              {pipeline.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{pipeline.description}</p>
              )}
            </div>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800 rounded-xl p-1">
          <button
            onClick={() => setViewMode('kanban')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'kanban'
                ? 'bg-white dark:bg-gray-700 text-lime-700 dark:text-lime-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
            }`}
          >
            <LayoutGrid className="w-4 h-4" />
            Board
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'list'
                ? 'bg-white dark:bg-gray-700 text-lime-700 dark:text-lime-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
            }`}
          >
            <List className="w-4 h-4" />
            List
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="flex items-center gap-6 mb-6 text-sm">
        <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <span className="font-medium text-gray-900 dark:text-gray-100">{totalDeals}</span>
          <span className="text-gray-500 dark:text-gray-400 ml-1.5">deals</span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <DollarSign className="w-3.5 h-3.5 text-gray-400" />
          <span className="font-medium text-gray-900 dark:text-gray-100">{formattedTotal}</span>
          <span className="text-gray-500 dark:text-gray-400">total</span>
        </div>
      </div>

      {/* View Content */}
      {viewMode === 'kanban' ? (
        <PipelineBoard
          stages={pipeline.stages || []}
          deals={deals}
          onMoveDeal={handleMoveDeal}
          onDealClick={handleDealClick}
        />
      ) : (
        <PipelineListView
          deals={deals}
          stages={pipeline.stages || []}
          onDealClick={handleDealClick}
        />
      )}
    </div>
  );
}
